import { useRef, useState } from 'react';
import type { Editor } from '../editor';
import { parseDxfFile, exportDxfBlob } from '../format/dxfAdapter';
import { parsePdfFile } from '../format/pdfAdapter';
import { exportSvg } from '../format/svgExport';
import { downloadBlob, exportPdf, exportPng } from '../format/printer';
import type { CadDocument } from '../model/document';
import type { ConversionWarning } from '../format/adapter';
import { PAPER_MM, STANDARD_SCALES, type Orientation, type PaperSize } from '../format/pdfExport';
import { Modal, ModalCancel } from '../../shell/Modal';
import { newEntityId } from '../model/document';
import type { CadOp } from '../ops/operation';
import { createUnderlayFromPdf } from '../../data/uploadRepo';

/**
 * 파일 메뉴 — 가져오기 / 내보내기 (작업지시서 P3-07 · P3-09 · P3-11 · P3-12)
 *
 * DXF/PDF 는 브라우저에서 바로 읽고 쓴다 (순수 TS·pdfjs-dist).
 * DWG/DWT 는 변환 워커를 거쳐야 하므로 업로드 → 큐 → 결과 수신 경로를 탄다.
 */
export function FileMenu({
  editor,
  drawingName,
  versionLabel,
  editable,
  isDraft,
  projectId,
  drawingId,
  versionId,
}: {
  editor: Editor;
  drawingName: string;
  versionLabel: string;
  editable: boolean;
  isDraft: boolean;
  /** 부착(Attach) 이미지 업로드 경로에 필요하다(Storage 정책이 project_id 로 멤버십을 본다). */
  projectId: string;
  drawingId: string;
  versionId: string;
}) {
  const [openExport, setOpenExport] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<ConversionWarning[] | null>(null);
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const [openAttach, setOpenAttach] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  /** DXF/PDF 가져오기 결과를 현재 도면에 덧붙인다. 통째로 갈아엎지 않는다 — 되돌리기 어렵다. */
  function commitImportedDoc(doc: CadDocument, sourceLabel: string): number {
    const ops: CadOp[] = [];
    const added = doc.entities.map((x) => ({ ...x, id: newEntityId() }));

    const have = new Set(editor.scene.doc.layers.map((l) => l.code));
    const newLayers = doc.layers.filter((l) => !have.has(l.code));
    if (newLayers.length > 0) {
      ops.push({
        kind: 'layer',
        command: 'IMPORT',
        before: editor.scene.doc.layers.map((l) => ({ ...l })),
        after: [...editor.scene.doc.layers.map((l) => ({ ...l })), ...newLayers],
      });
    }
    ops.push({ kind: 'add', command: 'IMPORT', entities: added });

    editor.scene.commit('IMPORT', ops);
    editor.zoomExtents();
    editor.write(
      `${sourceLabel} 가져오기 완료 — 객체 ${added.length}개` +
        (newLayers.length > 0 ? `, 레이어 ${newLayers.length}개 추가` : '')
    );
    return added.length;
  }

  /* -------------------------------------------------------- 가져오기 */

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일을 다시 고를 수 있게
    if (!file) return;

    const ext = file.name.toLowerCase().split('.').pop();

    if (ext === 'dwg' || ext === 'dwt') {
      // 브라우저에서는 DWG 를 읽을 수 없다. 워커 경로가 필요하다.
      // 여기(편집기 안)는 "지금 연 도면에 즉시 덧붙이기" 라 동기 처리만 받는다.
      // DWG 변환은 몇 초~몇 분 걸리는 비동기 작업이라 구조가 다르다 —
      // 그래서 새 도면을 만드는 프로젝트 화면 쪽에만 그 입구를 둔다.
      editor.write(
        'DWG/DWT 는 변환 서버를 거쳐야 합니다. 프로젝트 화면(← 프로젝트)의 "파일에서 만들기"로 올리세요.'
      );
      return;
    }
    if (ext === 'pdf') {
      // PDF 는 축척을 먼저 물어봐야 한다(PdfImportScaleModal) — 그 뒤에 파싱한다.
      setPendingPdf(file);
      return;
    }
    if (ext !== 'dxf') {
      editor.write(`지원하지 않는 형식입니다: .${ext}`);
      return;
    }

    setBusy('DXF 읽는 중…');
    try {
      const { doc, warnings: w } = await parseDxfFile(file);

      if (doc.entities.length === 0) {
        editor.write('읽을 수 있는 객체가 없습니다.');
        setWarnings(w.length > 0 ? w : null);
        return;
      }

      commitImportedDoc(doc, file.name);
      setWarnings(w.length > 0 ? w : null);
    } catch (err) {
      editor.write(`가져오기 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function importPdf(file: File, scaleDenom: number) {
    setPendingPdf(null);
    setBusy('PDF 읽는 중…');
    try {
      const { doc, warnings: w } = await parsePdfFile(file, { scaleDenom });

      if (doc.entities.length === 0) {
        editor.write('읽을 수 있는 벡터 객체가 없습니다 — 스캔본이라면 부착(Attach) 을 쓰세요.');
        setWarnings(w.length > 0 ? w : null);
        return;
      }

      commitImportedDoc(doc, file.name);
      setWarnings(w.length > 0 ? w : null);
    } catch (err) {
      editor.write(`PDF 가져오기 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function attachPdf(file: File, scaleDenom: number) {
    setOpenAttach(false);
    setBusy('부착 이미지 만드는 중…');
    try {
      const underlay = await createUnderlayFromPdf({
        projectId,
        drawingId,
        versionId,
        file,
        scaleDenom,
        center: editor.viewport.center,
      });

      const before = editor.scene.doc.underlays.map((u) => ({ ...u }));
      const after = [...before, underlay];
      editor.scene.commit('ATTACH', [
        { kind: 'meta', command: 'ATTACH', before: { underlays: before }, after: { underlays: after } },
      ]);
      editor.write(`${file.name} 부착 완료 — 참조용 이미지, 편집 대상이 아닙니다`);
    } catch (err) {
      editor.write(`부착 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  /* -------------------------------------------------------- 내보내기 */

  const safeName = drawingName.replace(/[\\/:*?"<>|]/g, '_');

  async function doExport(kind: 'dxf' | 'svg', paper?: PaperSize) {
    void paper;
    setBusy(`${kind.toUpperCase()} 만드는 중…`);
    try {
      if (kind === 'dxf') {
        const blob = await exportDxfBlob(editor.scene.doc);
        downloadBlob(blob, `${safeName}_${versionLabel}.dxf`);
      } else {
        const svg = exportSvg(editor.scene.doc, { background: '#FFFFFF' });
        downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), `${safeName}_${versionLabel}.svg`);
      }
      editor.write(`${kind.toUpperCase()} 내보내기 완료`);
    } catch (err) {
      editor.write(`내보내기 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function doPrint(kind: 'pdf' | 'png', paper: PaperSize, orientation: Orientation) {
    setBusy(`${kind.toUpperCase()} 만드는 중…`);
    try {
      const opt = {
        paper,
        orientation,
        dpi: 300,
        title: drawingName,
        subtitle: editor.scene.doc.layers.length > 0 ? `레이어 ${editor.scene.doc.layers.length}종` : '',
        versionLabel,
        draftWatermark: isDraft,
      };
      const r = kind === 'pdf' ? await exportPdf(editor.scene.doc, opt) : await exportPng(editor.scene.doc, opt);
      downloadBlob(r.blob, `${safeName}_${versionLabel}_1-${r.scaleDenom}.${kind}`);
      editor.write(`${kind.toUpperCase()} 내보내기 완료 — 축척 1:${r.scaleDenom}`);
      setOpenExport(false);
    } catch (err) {
      editor.write(`출력 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".dxf,.dwg,.dwt,.pdf"
        style={{ display: 'none' }}
        onChange={(e) => void onPickFile(e)}
      />

      <button
        type="button"
        className="cad-btn"
        disabled={!editable || busy !== null}
        onClick={() => fileRef.current?.click()}
        title="DXF · PDF 파일을 현재 도면에 편집 가능한 객체로 덧붙입니다 (PDF 는 PDFIMPORT)"
      >
        ↓ 가져오기
      </button>

      <button
        type="button"
        className="cad-btn"
        disabled={!editable || busy !== null}
        onClick={() => setOpenAttach(true)}
        title="PDF 한 페이지를 참조용 배경 이미지로 붙입니다 (편집 대상 아님)"
      >
        부착
      </button>

      <button
        type="button"
        className="cad-btn"
        disabled={busy !== null}
        onClick={() => setOpenExport(true)}
        title="DXF · SVG · PDF · PNG 로 내보냅니다"
      >
        ↑ 내보내기
      </button>

      {busy && <span className="cad-save-state">{busy}</span>}

      {openExport && (
        <ExportDialog
          onClose={() => setOpenExport(false)}
          onFile={(k) => void doExport(k)}
          onPrint={(k, p, o) => void doPrint(k, p, o)}
          busy={busy !== null}
        />
      )}

      {warnings && (
        <WarningDialog warnings={warnings} onClose={() => setWarnings(null)} />
      )}

      {pendingPdf && (
        <PdfScaleModal
          title="PDF 가져오기 (PDFIMPORT)"
          desc={`${pendingPdf.name} — 이 PDF 가 인쇄된 축척을 고르세요. PDF 좌표는 종이 크기라서, 실제 도면 크기를 맞추려면 축척이 필요합니다.`}
          confirmLabel="가져오기"
          onCancel={() => setPendingPdf(null)}
          onConfirm={(scaleDenom) => void importPdf(pendingPdf, scaleDenom)}
        />
      )}

      {openAttach && (
        <AttachModal onCancel={() => setOpenAttach(false)} onConfirm={(f, s) => void attachPdf(f, s)} />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * 내보내기 대화상자
 * -------------------------------------------------------------------------- */

function ExportDialog({
  onClose,
  onFile,
  onPrint,
  busy,
}: {
  onClose: () => void;
  onFile: (kind: 'dxf' | 'svg') => void;
  onPrint: (kind: 'pdf' | 'png', paper: PaperSize, orientation: Orientation) => void;
  busy: boolean;
}) {
  const [paper, setPaper] = useState<PaperSize>('A1');
  const [orientation, setOrientation] = useState<Orientation>('landscape');

  return (
    <Modal title="내보내기" desc="용도에 맞는 형식을 고르세요." onClose={onClose} input={false}>
      <div className="export-group">
        <div className="export-group__title">편집용 (다른 CAD 에서 열기)</div>
        <button type="button" className="btn" disabled={busy} onClick={() => onFile('dxf')}>
          DXF — AutoCAD 등에서 편집 가능
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => onFile('svg')}>
          SVG — 벡터 이미지 (한글 유지)
        </button>
        <p className="field__hint">
          DWG 로 내보내려면 변환 서버가 필요합니다. 도면 목록의 내보내기 요청을 이용하세요.
        </p>
      </div>

      <div className="export-group">
        <div className="export-group__title">인쇄용</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label className="field">
            <span className="field__label">용지</span>
            <select
              className="select"
              value={paper}
              onChange={(e) => setPaper(e.target.value as PaperSize)}
            >
              {(Object.keys(PAPER_MM) as PaperSize[]).map((p) => (
                <option key={p} value={p}>
                  {p} ({PAPER_MM[p].w}×{PAPER_MM[p].h}mm)
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">방향</span>
            <select
              className="select"
              value={orientation}
              onChange={(e) => setOrientation(e.target.value as Orientation)}
            >
              <option value="landscape">가로</option>
              <option value="portrait">세로</option>
            </select>
          </label>
        </div>

        <button
          type="button"
          className="btn btn--primary"
          disabled={busy}
          onClick={() => onPrint('pdf', paper, orientation)}
        >
          PDF — 축척 자동 · 제목란
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => onPrint('png', paper, orientation)}
        >
          PNG — 300dpi 이미지
        </button>
        <p className="field__hint">
          축척은 용지에 맞춰 표준 축척(1:50 · 1:100 · 1:200 …) 중에서 자동 선택되며,
          파일명과 제목란에 표기됩니다.
        </p>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- *
 * 변환 경고
 * -------------------------------------------------------------------------- */

export const WARNING_LABEL: Record<string, string> = {
  unsupported_entity: '변환하지 못한 객체',
  font_substituted: '글꼴 대체',
  xref_missing: '외부 참조 누락',
  dynamic_block_flattened: '동적 블록 단순화',
  precision_rounded: '단위 추정',
  layer_created: '레이어 자동 생성',
  binary_dxf: '바이너리 DXF',
  parse_recovered: '형식 오류 복구',
  curve_approximated: '곡선을 다각형으로 근사',
  raster_skipped: 'PDF 안 래스터 이미지 건너뜀',
};

/** 변환 경고 목록. 가져오기 결과 화면에서 공통으로 쓴다(P3-15 업로드 흐름도 재사용). */
export function WarningDialog({
  warnings,
  onClose,
}: {
  warnings: ConversionWarning[];
  onClose: () => void;
}) {
  return (
    <Modal
      title="가져오기 결과"
      desc="원본 파일은 변경되지 않았습니다."
      onClose={onClose}
      input={false}
      footer={
        <button type="button" className="btn btn--primary" onClick={onClose}>
          확인
        </button>
      }
    >
      <ul className="warn-list">
        {warnings.map((w, i) => (
          <li key={`${w.code}-${w.subject ?? i}`} className="warn-list__item">
            <span className="warn-list__badge">{WARNING_LABEL[w.code] ?? w.code}</span>
            <span>
              {w.message}
              {w.count && w.count > 1 && <b> ({w.count}건)</b>}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- *
 * PDF 축척 선택 (PDFIMPORT · Attach 공용)
 * -------------------------------------------------------------------------- */

const CUSTOM_SCALE = 'custom';

/**
 * 축척 고르기 — 표준 축척 + **직접 입력**.
 *
 * ★ 직접 입력이 반드시 필요한 이유
 *   오토캐드에서 "용지에 맞춤(Fit to paper)" 으로 출력하면 1:153.5 같은
 *   비표준 배율이 나온다. 실제 현장 PDF 가 그랬고, 목록에만 의존하면
 *   맞는 값을 **고를 수가 없어서** 도면 전체가 일정 비율로 어긋난다.
 */
export function PdfScalePicker({
  id,
  scaleDenom,
  onChange,
}: {
  id: string;
  scaleDenom: number;
  onChange: (v: number) => void;
}) {
  const [custom, setCustom] = useState(!STANDARD_SCALES.includes(scaleDenom));

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        이 PDF 의 인쇄 축척
      </label>
      <select
        id={id}
        className="select"
        value={custom ? CUSTOM_SCALE : String(scaleDenom)}
        onChange={(e) => {
          if (e.target.value === CUSTOM_SCALE) {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange(Number(e.target.value));
        }}
      >
        {STANDARD_SCALES.map((s) => (
          <option key={s} value={s}>
            1:{s}
          </option>
        ))}
        <option value={CUSTOM_SCALE}>직접 입력 (비표준 배율)</option>
      </select>

      {custom && (
        <input
          className="input"
          type="number"
          min="0.01"
          step="any"
          style={{ marginTop: 6 }}
          value={scaleDenom}
          onChange={(e) => onChange(Number(e.target.value))}
          placeholder="예: 153.5"
          aria-label="축척 분모 직접 입력"
        />
      )}

      <span className="field__hint">
        축척을 모르면 1:100 그대로 가져온 뒤, 편집기에서 <b>축척 보정(CAL)</b> 명령으로 실제
        치수를 아는 구간 한 군데만 재면 도면 전체가 자동으로 맞춰집니다.
      </span>
    </div>
  );
}

/** PDF 좌표(pt)는 종이 크기다 — 실제 도면 크기로 바꾸려면 인쇄 축척을 알아야 한다. */
function PdfScaleModal({
  title,
  desc,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  title: string;
  desc: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: (scaleDenom: number) => void;
}) {
  const [scaleDenom, setScaleDenom] = useState(100);

  return (
    <Modal
      title={title}
      desc={desc}
      onClose={onCancel}
      footer={
        <>
          <ModalCancel>취소</ModalCancel>
          <button type="button" className="btn btn--primary" onClick={() => onConfirm(scaleDenom)}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <PdfScalePicker id="pdf-scale" scaleDenom={scaleDenom} onChange={setScaleDenom} />
    </Modal>
  );
}

/* -------------------------------------------------------------------------- *
 * 부착(Attach) — 참조용, 편집 불가
 * -------------------------------------------------------------------------- */

function AttachModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (file: File, scaleDenom: number) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [scaleDenom, setScaleDenom] = useState(100);

  return (
    <Modal
      title="부착 (Attach)"
      desc="PDF 한 페이지를 배경 그림으로 붙입니다. 참조용일 뿐 편집·선택 대상이 아닙니다."
      onClose={onCancel}
      footer={
        <>
          <ModalCancel>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!file}
            onClick={() => file && onConfirm(file, scaleDenom)}
          >
            부착
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="attach-file">
          PDF 파일 *
        </label>
        <input
          id="attach-file"
          className="input"
          type="file"
          accept=".pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>
      <PdfScalePicker id="attach-scale" scaleDenom={scaleDenom} onChange={setScaleDenom} />
      <p className="field__hint">
        현재 화면 중심에 배치됩니다. 나중에 사이드 패널의 첨부 탭에서 지우거나 숨길 수 있습니다.
      </p>
    </Modal>
  );
}
